import { Users, Plus, Shield, GitBranch, ShoppingBag, MessageSquare, Building, ChevronRight, Eye, Clock, AlertTriangle, CheckCircle, UserPlus, Settings, BarChart3, Search } from 'lucide-react';
import ROIIndicator from './ROIIndicator';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTranslation } from 'react-i18next';
import PendingApprovalsQueue from './PendingApprovalsQueue';
import { useEscalations } from '../hooks/useEscalations';

interface CompanyDashboardViewProps {
    onNavigate: (view: string) => void;
}

// ── Helper: relative time from ISO string ──────────────────────────────────
function getRelativeTime(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(ms / 3600000);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(ms / 86400000)}d ago`;
}

export default function CompanyDashboardView({ onNavigate }: CompanyDashboardViewProps) {
    const { user } = useAuth();
    const { teams, currentWorkspace } = useWorkspace();
    const { t } = useTranslation('dashboard');

    // ── Live escalation data — handle auth errors gracefully ────────────
    const { escalations, loading: escLoading, error: escError } = useEscalations({ status: 'PENDING' });
    // When escalations fail (401 etc), treat as "no data" rather than showing errors
    const hasEscalationData = !escError && !escLoading;
    const pendingEscalations = hasEscalationData ? escalations.length : 0;
    const riskFlags = hasEscalationData ? escalations.filter(e => e.riskLevel === 'high').length : 0;
    const governanceHealth = pendingEscalations === 0 ? 100
        : Math.max(0, 100 - (riskFlags * 15) - (pendingEscalations * 3));
    const lastEscalationTime = hasEscalationData && escalations.length > 0
        ? getRelativeTime(escalations[0].createdAt)
        : null;

    const getCompanyName = () => {
        if (currentWorkspace?.name) return currentWorkspace.name;
        if (!user?.displayName) return user?.email?.split('@')[0] || 'Company';
        return user.displayName;
    };

    // ── Team stats ──────────────────────────────────────────────────────
    const totalReplicas = teams.reduce((sum, team) => sum + (team._count?.members || 0), 0);
    const activeTeams = teams.filter(team => team.status === 'ACTIVE').length;
    const teamsSummary = teams.length > 0
        ? t('company_dashboard.teams_supported', { count: activeTeams })
        : t('company_dashboard.no_teams_yet');
    const escalationRate = hasEscalationData && escalations.length > 0
        ? Math.round((riskFlags / escalations.length) * 100)
        : 0;

    return (
        <div className="h-full flex flex-col gap-3 max-w-6xl mx-auto">

            {/* ═══ ROW 1: Company Header ═══ */}
            <div className="flex items-center gap-4 rounded-2xl bg-gradient-to-r from-hig-bg-secondary via-hig-bg-tertiary/50 to-hig-bg-secondary border border-hig-divider px-5 py-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                    {getCompanyName().charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="text-lg font-bold text-hig-text-primary truncate">{getCompanyName()}</h1>
                        <span className="badge bg-indigo-500/10 text-indigo-400 border-indigo-500/20 text-[10px] px-2 py-0.5">{t('company_dashboard.enterprise')}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-hig-text-secondary">
                        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-hig-success" />{t('company_dashboard.active_replicas_count', { count: totalReplicas })}</span>
                        <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-hig-accent" />{teamsSummary}</span>
                    </div>
                </div>
                <button onClick={() => onNavigate('marketplace')} className="btn-primary px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 flex-shrink-0">
                    <Plus size={14} />
                    {t('company_dashboard.hire_ai_agents')}
                </button>
            </div>

            {/* ═══ ROW 2: Main Grid ═══ */}
            <div className="flex-1 grid grid-cols-12 gap-3 min-h-0">

                {/* ──── Left Column (7 cols): Governance + Teams ──── */}
                <div className="col-span-7 flex flex-col gap-3 min-h-0 overflow-auto">

                    {/* Governance Panel */}
                    <div className="card border-l-3 border-hig-warning overflow-hidden">
                        <div className="px-4 py-3 border-b border-hig-divider flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Shield size={18} className="text-hig-warning" />
                                <div>
                                    <h2 className="text-sm font-bold text-hig-text-primary">{t('company_dashboard.decision_guardians')}</h2>
                                    <p className="text-[11px] text-hig-text-secondary">{t('authority_signal.subtext')}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {hasEscalationData && pendingEscalations > 0 && (
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-hig-warning/10 border border-hig-warning/20">
                                        <Clock size={12} className="text-hig-warning" />
                                        <span className="text-[11px] font-medium text-hig-warning">{pendingEscalations} {t('company_dashboard.pending_approvals')}</span>
                                    </div>
                                )}
                                {hasEscalationData && riskFlags > 0 && (
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/10 border border-red-500/20">
                                        <AlertTriangle size={12} className="text-red-400" />
                                        <span className="text-[11px] font-medium text-red-400">{riskFlags} Risk</span>
                                    </div>
                                )}
                                {(hasEscalationData && pendingEscalations === 0) && (
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-hig-success/10 border border-hig-success/20">
                                        <CheckCircle size={12} className="text-hig-success" />
                                        <span className="text-[11px] font-medium text-hig-success">{t('company_dashboard.all_clear')}</span>
                                    </div>
                                )}
                                {escLoading && (
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-hig-bg-tertiary border border-hig-divider">
                                        <Clock size={12} className="text-hig-text-tertiary animate-spin" />
                                        <span className="text-[11px] text-hig-text-tertiary">Loading...</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4">
                            {escError ? (
                                /* Graceful fallback when escalation API is unavailable */
                                <div className="text-center py-6">
                                    <CheckCircle size={28} className="text-hig-success mx-auto mb-2" />
                                    <p className="text-sm font-medium text-hig-text-primary">{t('company_dashboard.all_clear')}</p>
                                    <p className="text-xs text-hig-text-tertiary mt-1">No pending decisions require your attention</p>
                                </div>
                            ) : (
                                <PendingApprovalsQueue />
                            )}
                        </div>

                        <div className="px-4 py-2.5 bg-hig-bg-tertiary border-t border-hig-divider flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1.5">
                                    <CheckCircle size={13} className="text-hig-success" />
                                    <span className="text-[11px] text-hig-text-secondary">{t('company_dashboard.governance_health')}: <span className={`font-medium ${governanceHealth >= 80 ? 'text-hig-success' : governanceHealth >= 50 ? 'text-hig-warning' : 'text-hig-error'}`}>{governanceHealth}%</span></span>
                                </div>
                                {lastEscalationTime && (
                                    <div className="flex items-center gap-1.5">
                                        <Clock size={13} className="text-hig-text-tertiary" />
                                        <span className="text-[11px] text-hig-text-secondary">{t('company_dashboard.last_escalation')}: {lastEscalationTime}</span>
                                    </div>
                                )}
                            </div>
                            <button onClick={() => onNavigate('audit-logs')} className="text-[11px] text-hig-accent hover:underline flex items-center gap-1">
                                <Eye size={12} /> {t('company_dashboard.view_audit_logs')}
                            </button>
                        </div>
                    </div>

                    {/* Teams Section */}
                    <div className="card overflow-hidden flex-1 min-h-0">
                        <div className="px-4 py-3 border-b border-hig-divider flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Users size={16} className="text-hig-accent" />
                                <h2 className="text-sm font-bold text-hig-text-primary">{t('company_dashboard.your_teams')}</h2>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => onNavigate('teams')} className="text-[11px] text-hig-accent hover:underline flex items-center gap-1">
                                    {t('company_dashboard.view_all')} <ChevronRight size={12} />
                                </button>
                                <button onClick={() => onNavigate('create-team')} className="btn-secondary text-xs px-3 py-1.5">
                                    <Plus size={12} className="mr-1" /> {t('company_dashboard.manage_teams')}
                                </button>
                            </div>
                        </div>

                        {teams.length === 0 ? (
                            <div className="p-8 text-center">
                                <Users size={28} className="text-hig-text-tertiary mx-auto mb-3" />
                                <h3 className="text-sm font-medium text-hig-text-primary mb-1">{t('company_dashboard.no_teams_yet')}</h3>
                                <p className="text-xs text-hig-text-secondary mb-4">{t('company_dashboard.create_first_team')}</p>
                                <button onClick={() => onNavigate('create-team')} className="btn-primary text-sm px-4 py-2">
                                    <Plus size={14} className="mr-1" /> {t('company_dashboard.create_first_team_btn')}
                                </button>
                            </div>
                        ) : (
                            <div className="divide-y divide-hig-divider overflow-auto">
                                {teams.map((team) => (
                                    <div key={team.id} className="px-4 py-3 hover:bg-hig-bg-elevated transition-colors cursor-pointer" onClick={() => onNavigate(`team-${team.id}`)}>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                                                    {team.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-semibold text-hig-text-primary">{team.name}</h3>
                                                    <p className="text-[11px] text-hig-text-secondary">
                                                        {team._count?.members || 0} {t('company_dashboard.members')}
                                                        {team.description && ` · ${team.description}`}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-hig-success/10 border border-hig-success/20">
                                                    <Shield size={10} className="text-hig-success" />
                                                    <span className="text-[10px] font-medium text-hig-success">{t('company_dashboard.guardian_active')}</span>
                                                </div>
                                                <ChevronRight size={14} className="text-hig-text-tertiary" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ──── Right Column (5 cols): Metrics + Enterprise Actions ──── */}
                <div className="col-span-5 flex flex-col gap-3 min-h-0 overflow-auto">

                    {/* Org Metrics — 2×2 grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="card p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-hig-accent/10 flex items-center justify-center">
                                    <BarChart3 size={16} className="text-hig-accent" />
                                </div>
                            </div>
                            <p className="text-xl font-bold text-hig-text-primary">{totalReplicas}</p>
                            <p className="text-[10px] uppercase tracking-wider text-hig-text-secondary font-medium mt-1">{t('company_dashboard.active_replicas')}</p>
                        </div>
                        <ROIIndicator compact />
                        <div className="card p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-hig-warning/10 flex items-center justify-center">
                                    <AlertTriangle size={16} className="text-hig-warning" />
                                </div>
                            </div>
                            <p className="text-xl font-bold text-hig-text-primary">{escalationRate}%</p>
                            <p className="text-[10px] uppercase tracking-wider text-hig-text-secondary font-medium mt-1">{t('company_dashboard.escalation_rate')}</p>
                        </div>
                        <div className="card p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-hig-success/10 flex items-center justify-center">
                                    <CheckCircle size={16} className="text-hig-success" />
                                </div>
                            </div>
                            <p className={`text-xl font-bold ${governanceHealth >= 80 ? 'text-hig-success' : governanceHealth >= 50 ? 'text-hig-warning' : 'text-hig-error'}`}>{governanceHealth}%</p>
                            <p className="text-[10px] uppercase tracking-wider text-hig-text-secondary font-medium mt-1">{t('company_dashboard.governance_health')}</p>
                        </div>
                    </div>

                    {/* Enterprise Management — No redundancy */}
                    <div className="flex flex-col gap-2">
                        <p className="text-[10px] uppercase tracking-wider text-hig-text-tertiary font-semibold px-1">{t('company_dashboard.manage_teams')}</p>

                        {/* Employee Accounts — PRIMARY action */}
                        <button onClick={() => onNavigate('employee-accounts')} className="card-interactive px-4 py-3 flex items-center gap-3 group text-left border-l-3 border-emerald-500">
                            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                                <UserPlus size={18} className="text-emerald-400" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-hig-text-primary">{t('company_dashboard.employee_accounts')}</p>
                                <p className="text-[11px] text-hig-text-tertiary">{t('company_dashboard.invite_link_employees')}</p>
                            </div>
                            <ChevronRight size={14} className="text-hig-text-tertiary ml-auto group-hover:translate-x-1 transition-transform" />
                        </button>

                        {/* Augmented Org Chart */}
                        <button onClick={() => onNavigate('org-chart')} className="card-interactive px-4 py-3 group text-left border-l-3 border-cyan-500">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                                    <GitBranch size={18} className="text-cyan-400" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-hig-text-primary">{t('company_dashboard.team_hierarchy')}</p>
                                    <p className="text-[11px] text-hig-text-tertiary">{t('company_dashboard.augmented_org_chart')}</p>
                                </div>
                                <ChevronRight size={14} className="text-hig-text-tertiary group-hover:translate-x-1 transition-transform" />
                            </div>
                            {/* Three-state indicators */}
                            <div className="flex items-center gap-3 mt-1.5 pl-12">
                                <div className="flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                                    <span className="text-[10px] text-hig-text-tertiary">{t('company_dashboard.state_human')}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                                    <span className="text-[10px] text-hig-text-tertiary">{t('company_dashboard.state_augmented')}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                                    <span className="text-[10px] text-hig-text-tertiary">{t('company_dashboard.state_autonomous')}</span>
                                </div>
                            </div>
                        </button>

                        {/* Data Permissions */}
                        <button onClick={() => onNavigate('permissions')} className="card-interactive px-4 py-3 flex items-center gap-3 group text-left">
                            <div className="w-9 h-9 rounded-xl bg-hig-bg-tertiary flex items-center justify-center flex-shrink-0">
                                <Shield size={18} className="text-hig-text-secondary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-hig-text-primary">{t('company_dashboard.data_permissions')}</p>
                                <p className="text-[11px] text-hig-text-tertiary">{t('company_dashboard.control_data_access')}</p>
                            </div>
                            <ChevronRight size={14} className="text-hig-text-tertiary ml-auto group-hover:translate-x-1 transition-transform" />
                        </button>

                        {/* Company Settings */}
                        <button onClick={() => onNavigate('settings')} className="card-interactive px-4 py-3 flex items-center gap-3 group text-left">
                            <div className="w-9 h-9 rounded-xl bg-hig-bg-tertiary flex items-center justify-center flex-shrink-0">
                                <Settings size={18} className="text-hig-text-secondary" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-hig-text-primary">{t('company_dashboard.company_settings')}</p>
                                <p className="text-[11px] text-hig-text-tertiary">{t('company_dashboard.configure_organization_settings')}</p>
                            </div>
                            <ChevronRight size={14} className="text-hig-text-tertiary ml-auto group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    {/* Sovereignty Reminder — Compact footer */}
                    <div className="rounded-xl bg-gradient-to-r from-hig-bg-secondary to-hig-bg-tertiary border border-hig-divider px-4 py-2.5 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                            <Building size={16} className="text-indigo-400" />
                        </div>
                        <p className="text-[11px] text-hig-text-tertiary">
                            {t('company_dashboard.hire_not_own')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
